import { Card, message, Skeleton } from "antd5";
import { Text } from "styles/utility-components";

import { DetailsHeader, DetailsPage, DetailsSection } from "components/app_layout/DetailsLayout";
import Button from "components/ui/button/Button";
import { copyToClipboard } from "lib/clipboard";
import { type PublicReport, usePublicReports } from "lib/hooks/api/reports/usePublicReports";
import { EventNames, useTracking } from "lib/tracking";

import css from "./IndustryReportsPage.module.scss";

import placeholderImage from "../../../assets/images/stotles_placeholder.svg";

export function IndustryReportsPage() {
  const { data, isLoading } = usePublicReports();
  const [messageApi, contextHolder] = message.useMessage();
  const { logEvent } = useTracking();

  const copyReportLink = async (report: PublicReport) => {
    const clipboardContent = {
      plain: report.pdfUrl
        ? report.pdfUrl
        : `https://app.stotles.com/reports/${report.id}?utm_source=share`,
    };
    const copiedToClipboard = await copyToClipboard(clipboardContent);
    logEvent(EventNames.industryReportLinkCopied, {
      "Report name": report.title,
      "Report URL": report.pdfUrl ? report.pdfUrl : `/reports/${report.id}`,
    });
    if (copiedToClipboard) {
      void messageApi.success("Copied!", 2);
    } else {
      void messageApi.error(`Could not copy link to clipboard`, 2);
    }
  };

  return (
    <DetailsPage>
      <DetailsHeader borderBottom={false}>
        <Text h1>Industry reports</Text>
      </DetailsHeader>
      <DetailsSection noBorder>
        {isLoading ? (
          <Skeleton />
        ) : (
          <div className={css.grid}>
            {data?.map((report) => (
              <Card
                cover={
                  <img
                    src={report.coverImageUrl || placeholderImage}
                    alt="report cover"
                    className={css.coverImage}
                  />
                }
                key={report.id}
                className={css.card}
                hoverable
                onClick={() => {
                  logEvent(EventNames.industryReportViewed, {
                    "Report name": report.title,
                    "Report URL": report.pdfUrl ? report.pdfUrl : `/reports/${report.id}`,
                  });
                  window.open(report.pdfUrl ? report.pdfUrl : `/reports/${report.id}`, "_blank");
                }}
              >
                <Card.Meta
                  title={report.title}
                  description={
                    <div className={css.descriptionContainer}>
                      <p className={css.description}>{report.description || report.adminNotes}</p>
                      <div className={css.cardActions}>
                        <Button
                          as="a"
                          href={report.pdfUrl ? report.pdfUrl : `/reports/${report.id}`}
                          target="_blank"
                          className={css.customIconButton}
                          icon="share03"
                          onClick={(e) => {
                            e.stopPropagation();
                            logEvent(EventNames.industryReportViewed, {
                              "Report name": report.title,
                              "Report URL": report.pdfUrl ? report.pdfUrl : `/reports/${report.id}`,
                            });
                          }}
                        >
                          View
                        </Button>
                        <Button
                          buttonType="secondary"
                          title="Copy link"
                          icon="link01"
                          onClick={async (e) => {
                            e.stopPropagation();
                            await copyReportLink(report);
                          }}
                        />
                      </div>
                    </div>
                  }
                />
              </Card>
            ))}
          </div>
        )}
      </DetailsSection>
      {contextHolder}
    </DetailsPage>
  );
}
